import { BugOutlined, CheckCircleOutlined, CloseCircleOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Card, Modal, Tag } from "antd";
import { useEffect, useState } from "react";

/**
 * InstallPrompt Component with Enhanced Debugging
 * Shows a prompt to install the PWA when the browser triggers the before install prompt event
 * Only appears on supported browsers (Chrome, Edge, etc.)
 *
 * Debug Mode: Press Ctrl+Shift+D to toggle debug panel
 */

interface DebugInfo {
  protocol: string;
  domain: string;
  browser: string;
  isStandalone: boolean;
  serviceWorkerRegistered: boolean;
  manifestLoaded: boolean;
  beforeInstallPromptFired: boolean;
  sessionDismissed: boolean;
  timestamp: string;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    protocol: "",
    domain: "",
    browser: "",
    isStandalone: false,
    serviceWorkerRegistered: false,
    manifestLoaded: false,
    beforeInstallPromptFired: false,
    sessionDismissed: false,
    timestamp: "",
  });

  useEffect(() => {
    const initDebugInfo = async () => {
      // Check service worker registration
      let swRegistered = false;
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          swRegistered = registration !== undefined;
          // console.log("[InstallPrompt] Service Worker Registration:", registration);
        } catch (error) {
          console.error("[InstallPrompt] Service Worker check failed:", error);
        }
      }

      // Check manifest loading
      let manifestLoaded = false;
      try {
        const response = await fetch("/manifest.json");
        manifestLoaded = response.ok;
        if (manifestLoaded) {
          const manifest = await response.json();
          console.log("[InstallPrompt] Manifest loaded:", manifest);
        }
      } catch (error) {
        console.error("[InstallPrompt] Manifest check failed:", error);
      }

      const info: DebugInfo = {
        protocol: window.location.protocol,
        domain: window.location.hostname,
        browser: navigator.userAgent,
        isStandalone: window.matchMedia("(display-mode: standalone)").matches,
        serviceWorkerRegistered: swRegistered,
        manifestLoaded: manifestLoaded,
        beforeInstallPromptFired: false,
        sessionDismissed: !!sessionStorage.getItem("installPromptDismissed"),
        timestamp: new Date().toISOString(),
      };

      // console.log("[InstallPrompt] ========================================");
      // console.log("[InstallPrompt] PWA Installation Debug Info");
      // console.log("[InstallPrompt] ========================================");
      // console.log("[InstallPrompt] Protocol:", info.protocol);
      // console.log("[InstallPrompt] Domain:", info.domain);
      // console.log("[InstallPrompt] Browser:", info.browser);
      // console.log("[InstallPrompt] Is Standalone:", info.isStandalone);
      // console.log("[InstallPrompt] Service Worker Registered:", info.serviceWorkerRegistered);
      // console.log("[InstallPrompt] Manifest Loaded:", info.manifestLoaded);
      // console.log("[InstallPrompt] Session Dismissed:", info.sessionDismissed);
      // console.log("[InstallPrompt] ========================================");

      setDebugInfo(info);
    };

    initDebugInfo();

    // Enhanced beforeInstallPrompt handler with detailed logging
    const handler = (e: Event) => {
      console.log("[InstallPrompt] ✅ beforeInstallPrompt event FIRED!", e);
      console.log("[InstallPrompt] Event details:", {
        type: e.type,
        bubbles: e.bubbles,
        cancelable: e.cancelable,
        defaultPrevented: e.defaultPrevented,
      });

      // Prevent the mini-infoBar from appearing on mobile
      e.preventDefault();
      console.log("[InstallPrompt] Default prevented - mini-infoBar suppressed");

      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      console.log("[InstallPrompt] Event stashed for later use");

      // Update debug info
      setDebugInfo(prev => ({
        ...prev,
        beforeInstallPromptFired: true,
        timestamp: new Date().toISOString(),
      }));

      // Show the install prompt after a short delay
      setTimeout(() => {
        console.log("[InstallPrompt] Showing install prompt after 3s delay");
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener("beforeInstallPrompt", handler);
    console.log("[InstallPrompt] ✓ Event listener registered for \"beforeInstallPrompt\"");
    console.log("[InstallPrompt] Waiting for event to fire...");

    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      console.log("[InstallPrompt] ⚠️ App already installed (running in standalone mode)");
      setShowPrompt(false);
    } else {
      console.log("[InstallPrompt] App not yet installed (running in browser)");
    }

    // Keyboard shortcut to toggle debug panel (Ctrl+Shift+D)
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        setDebugMode(prev => {
          const newMode = !prev;
          console.log("[InstallPrompt] Debug mode toggled:", newMode ? "ON" : "OFF");
          return newMode;
        });
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    console.log("[InstallPrompt] Debug mode keyboard shortcut registered (Ctrl+Shift+D)");

    // Log eligibility check after 5 seconds
    setTimeout(() => {
      if (!deferredPrompt) {
        console.log("[InstallPrompt] ⚠️ WARNING: beforeInstallPrompt has NOT fired after 5 seconds");
        // console.log("[InstallPrompt] Possible reasons:");
        // console.log("[InstallPrompt]   1. Not using HTTPS (current:", window.location.protocol + ")");
        // console.log("[InstallPrompt]   2. Browser does not support PWA installation (check browser compatibility)");
        // console.log("[InstallPrompt]   3. App already installed");
        // console.log("[InstallPrompt]   4. User has not engaged enough with the site");
        // console.log("[InstallPrompt]   5. Service worker not properly registered");
        // console.log("[InstallPrompt]   6. Manifest file has errors or is not accessible");
        // console.log("[InstallPrompt]   7. Internal domain security policies blocking PWA features");
      }
    }, 5000);

    return () => {
      window.removeEventListener("beforeInstallPrompt", handler);
      window.removeEventListener("keydown", handleKeyPress);
      console.log("[InstallPrompt] Event listeners cleaned up");
    };
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.error("[InstallPrompt] ❌ Cannot install: deferredPrompt is null");
      return;
    }

    // console.log("[InstallPrompt] User clicked Install button");

    try {
      // Show the install prompt
      // console.log("[InstallPrompt] Calling prompt() on deferred event...");
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      // console.log("[InstallPrompt] Waiting for user choice...");
      const { outcome } = await deferredPrompt.userChoice;

      // console.log("[InstallPrompt] User choice outcome:", outcome);
      // if (outcome === "accepted") {
      //   console.log("[InstallPrompt] ✅ User ACCEPTED the install prompt");
      // } else {
      //   console.log("[InstallPrompt] ❌ User DISMISSED the install prompt");
      // }

      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setShowPrompt(false);
      // console.log("[InstallPrompt] Install flow completed, prompt hidden");
    } catch (error) {
      console.error("[InstallPrompt] ❌ Error during installation:", error);
    }
  };

  const handleDismiss = () => {
    // console.log("[InstallPrompt] User dismissed the install prompt");
    setShowPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem("installPromptDismissed", "true");
    // console.log("[InstallPrompt] Session storage updated - will not show again this session");
  };

  // Don't show if already dismissed in this session
  if (sessionStorage.getItem("installPromptDismissed")) {
    // console.log("[InstallPrompt] Prompt suppressed - dismissed in this session");
    return debugMode ? <DebugPanel debugInfo={debugInfo} /> : null;
  }

  if (!showPrompt || !deferredPrompt) {
    return debugMode ? <DebugPanel debugInfo={debugInfo} /> : null;
  }

  return (
    <>
      <Modal
        open={showPrompt}
        onCancel={handleDismiss}
        footer={[
          <Button key="later" onClick={handleDismiss} icon={<CloseOutlined />}>
            Maybe Later
          </Button>,
          <Button key="install" type="primary" icon={<DownloadOutlined />} onClick={handleInstall}>
            Install App
          </Button>,
        ]}
        width={400}
        centered
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <DownloadOutlined style={{ fontSize: 48, color: "#1890ff", marginBottom: 16 }} />
          <h2 style={{ marginBottom: 12 }}>Install DT-RadarPlus</h2>
          <p style={{ color: "#666", marginBottom: 0 }}>Install this app on your device for a better experience! You'll be able to:</p>
          <ul style={{ textAlign: "left", marginTop: 16, color: "#666" }}>
            <li>Access the app from your home screen</li>
            <li>Work offline with cached data</li>
            <li>Enjoy faster load times</li>
            <li>Get a native app-like experience</li>
          </ul>
        </div>
      </Modal>
      {debugMode && <DebugPanel debugInfo={debugInfo} />}
    </>
  );
}

/**
 * Debug Panel Component
 * Shows PWA installation eligibility checks
 */
function DebugPanel({ debugInfo }: { debugInfo: DebugInfo }) {
  const checks = [
    {
      name: "HTTPS Protocol",
      passed: debugInfo.protocol === "https:",
      value: debugInfo.protocol,
    },
    {
      name: "Service Worker Registered",
      passed: debugInfo.serviceWorkerRegistered,
      value: debugInfo.serviceWorkerRegistered ? "Yes" : "No",
    },
    {
      name: "Manifest Loaded",
      passed: debugInfo.manifestLoaded,
      value: debugInfo.manifestLoaded ? "Yes" : "No",
    },
    {
      name: "beforeInstallPrompt Fired",
      passed: debugInfo.beforeInstallPromptFired,
      value: debugInfo.beforeInstallPromptFired ? "Yes" : "No",
    },
    {
      name: "Not Already Installed",
      passed: !debugInfo.isStandalone,
      value: debugInfo.isStandalone ? "Installed" : "Not Installed",
    },
    {
      name: "Not Session Dismissed",
      passed: !debugInfo.sessionDismissed,
      value: debugInfo.sessionDismissed ? "Dismissed" : "Active",
    },
  ];

  const allPassed = checks.every(check => check.passed);

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 10000,
        maxWidth: 400,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }}
    >
      <Card
        title={
          <span>
            <BugOutlined style={{ marginRight: 8 }} />
            PWA Install Debug Panel
          </span>
        }
        size="small"
        extra={<Tag color={allPassed ? "success" : "error"}>{allPassed ? "All Checks Passed" : "Issues Detected"}</Tag>}
      >
        <div style={{ fontSize: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <strong>Domain:</strong> {debugInfo.domain}
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Browser:</strong> {debugInfo.browser.substring(0, 50)}...
          </div>
          <div style={{ marginBottom: 12 }}>
            <strong>Last Check:</strong> {new Date(debugInfo.timestamp).toLocaleTimeString()}
          </div>
          <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 12, marginTop: 12 }}>
            <strong>Eligibility Checks:</strong>
            <div style={{ marginTop: 8 }}>
              {checks.map((check, index) => (
                <div key={index} style={{ marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {check.passed ? <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} /> : <CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />}
                    {check.name}
                  </span>
                  <Tag color={check.passed ? "success" : "error"} style={{ marginLeft: 8 }}>
                    {check.value}
                  </Tag>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: "#999" }}>Press Ctrl+Shift+D to toggle this panel</div>
        </div>
      </Card>
    </div>
  );
}
